import { useEffect, useRef } from "react";
import { usePharmaFormFields } from "../context/pharmaFormFieldsContext";
import { useDynamicRefDocOptions } from "./useDynamicRefDocOptions";

interface FieldOptionsDef {
  refDocPath?: string;
  refDocFieldName?: string;
  options: { label: string; value: string }[];
  key: string;
}

interface UseResolvedFieldOptionsReturn {
  resolvedOptions: { label: string; value: string }[];
  optionsFingerprint: string;
}

/**
 * Resolves field options from either a dynamic Firestore path or static
 * config, and automatically resets the field value in `FormContext` whenever
 * the available options change.
 *
 * @param field      - Field config containing `options`, `key`, and optional
 *                     `refDocPath` / `refDocFieldName` for dynamic fetching.
 * @param emptyValue - Value to reset to when options change (`""` for
 *                     single-select fields, `[]` for multi-select).
 */
export const useResolvedFieldOptions = (
  field: FieldOptionsDef,
  emptyValue: "" | string[] = "",
): UseResolvedFieldOptionsReturn => {
  const { formFieldsData, setFormFieldsData } = usePharmaFormFields();
  const { options: dynamicOptions } = useDynamicRefDocOptions(
    field.refDocPath,
    field.refDocFieldName,
  );

  const valueFromFormFieldsData = formFieldsData?.[field.key]?.filledValue;

  const resolvedOptions =
    dynamicOptions.length > 0 ? dynamicOptions : field.options;

  const optionsFingerprint = JSON.stringify(resolvedOptions);
  const prevOptionsFingerprintRef = useRef(optionsFingerprint);
  const isOptionInvalid = Array.isArray(valueFromFormFieldsData)
    ? valueFromFormFieldsData.some(
        (v) => !resolvedOptions.some((option) => option.value === v),
      )
    : valueFromFormFieldsData &&
      !resolvedOptions.some(
        (option) => option.value === valueFromFormFieldsData,
      );

  useEffect(() => {
    if (prevOptionsFingerprintRef.current !== optionsFingerprint) {
      prevOptionsFingerprintRef.current = optionsFingerprint;
      setFormFieldsData((prev) => ({
        ...prev,
        [field.key]: {
          ...prev[field.key],
          filledValue: isOptionInvalid ? emptyValue : valueFromFormFieldsData,
          isFieldDirty: true,
        },
      }));
    }
  }, [
    optionsFingerprint,
    field.key,
    emptyValue,
    setFormFieldsData,
    isOptionInvalid,
  ]);

  return { resolvedOptions, optionsFingerprint };
};
